import { callV3 } from '@modernanimal/common';
import { removeTrailingZeros } from '@modernanimal/common/src/utils/clientOnboarding';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';

import {
    GET_BREEDS, GET_COLORS, CREATE_EXTERNAL_CLINIC, GET_EXTERNAL_CLINICS, CREATE_PATIENT,
    CREATE_INBOUND_MEDICAL_RECORD_REQUEST, PATCH_PATIENT,
    CREATE_ACCOUNT_CREDIT,
    CREATE_CLINICAL_RECORD,
    CREATE_INVOICE,
    CREATE_INVOICE_ITEMS,
    CREATE_TRANSACTION,
    CREATE_MEMBERSHIP,
    PATCH_INVOICE,
} from './patientOnboarding.v3.gql';
import {
    AppointmentInput,
    CreateExternalClinicMutation, CreateExternalClinicMutationVariables,
    CreateInboundMedicalRecordRequestMutation,
    CreateInboundMedicalRecordRequestMutationVariables,
    CreatePatientMutation, CreatePatientMutationVariables,
    CustomerBreedSpeciesEnum, CustomerColorSpeciesEnum,
    CustomerMembershipTierEnum,
    CustomerInboundMedicalRecordRequestStatusEnum, CustomerPatientSpeciesEnum,
    ExternalClinicInput, GetBreedsQuery,
    GetBreedsQueryVariables,
    GetColorsQuery,
    GetColorsQueryVariables, GetExternalClinicsQuery, GetExternalClinicsQueryVariables,
    InboundMedicalRecordRequestInput,
    PatchPatientMutation,
    PatchPatientMutationVariables, PatientInput,
    PatientInputPartial, StaffAppointmentAppointmentTypeEnum, StaffAppointmentSourceEnum,
    AccountCreditInput,
    CreateAccountCreditMutation,
    CreateAccountCreditMutationVariables,
    BillingAccountCreditCreditTypeEnum,
    PromoType,
    ClinicalRecordInput,
    CreateClinicalRecordMutation,
    CreateClinicalRecordMutationVariables,
    BillingInvoiceStatusEnum,
    InvoiceInput,
    CreateInvoiceMutation,
    CreateInvoiceMutationVariables,
    InvoiceItemInput,
    CreateInvoiceItemsMutation,
    CreateInvoiceItemsMutationVariables,
    BillingTransactionMethodEnum,
    TransactionInput,
    CreateTransactionMutation,
    CreateTransactionMutationVariables,
    HouseholdType,
    MembershipInput,
    CreateMembershipMutation,
    CreateMembershipMutationVariables,
    GetMembershipProductsAndComprehensiveExamQuery,
    InvoiceInputPartial,
    PatchInvoiceMutation,
    PatchInvoiceMutationVariables,
    GetMembershipProductsAndComprehensiveExamQueryVariables,
    BillingTransactionStatusEnum,
} from '../__generated__/graphql';
import { createAppointment } from '../clientOnboarding/clientOnboarding.helpers';
import { GET_MEMBERSHIP_PRODUCTS_AND_COMPREHENSIVE_EXAM } from '../clientOnboarding/clientOnboarding.v3.gql';
import { Promo } from '../clientOnboarding/PromoCodeInput';
import {
    ALL_ACCESS_PRODUCT_ID,
    ALL_ACCESS_UPGRADE_PRODUCT_ID,
    ESSENTIAL_PRODUCT_ID,
} from '../constants';
import {
    AppointmentStoreType, ClientStoreType, ExternalClinicStoreType, PatientStoreType
} from '../store';

type CreateMembershipResponse = {
    successful: boolean;
    data?: string;
    errorMessage?: string;
};

export type Membership = {
    productId: string;
    membershipTier: CustomerMembershipTierEnum;
    description: string;
    membershipPrice: string;
    discountedPrice?: string;
};

const tierToProductId: Partial<Record<CustomerMembershipTierEnum, string | undefined>> = {
    [CustomerMembershipTierEnum.Essential]: ESSENTIAL_PRODUCT_ID,
    [CustomerMembershipTierEnum.AllAccess]: ALL_ACCESS_PRODUCT_ID,
    [CustomerMembershipTierEnum.PayAsYouGo]: undefined,
};

export const getBreeds = async (species: CustomerBreedSpeciesEnum) => {
    const resp = await callV3<GetBreedsQuery, GetBreedsQueryVariables>(
        null, 'public', GET_BREEDS, { species }
    );

    return resp.data.breeds;
};

export const getColors = async (species: CustomerColorSpeciesEnum) => {
    const resp = await callV3<GetColorsQuery, GetColorsQueryVariables>(
        null, 'public', GET_COLORS, { species }
    );

    return resp.data.colors;
};

export const getMembershipProductsAndComprehensiveExam = async (token: string, locationId: string) => {
    const resp = await callV3<
        GetMembershipProductsAndComprehensiveExamQuery,
        GetMembershipProductsAndComprehensiveExamQueryVariables
    >(
        token, 'client', GET_MEMBERSHIP_PRODUCTS_AND_COMPREHENSIVE_EXAM, { locationId }
    );

    return resp.data;
};

const createPatient = async (token: string, input: PatientInput) => {
    try {
        const resp = await callV3<CreatePatientMutation, CreatePatientMutationVariables>(
            token, 'client', CREATE_PATIENT, { input }
        );

        return resp.data.createPatient;
    } catch (err) {
        console.error('Error in createPatient:', err);
        Sentry.captureException(err, {
            extra: {
                input,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createPatient'
            },
        });
        return null;
    }
};

const patchPatient = async (token: string, input: PatientInputPartial) => {
    try {
        const resp = await callV3<PatchPatientMutation, PatchPatientMutationVariables>(
            token, 'client', PATCH_PATIENT, { input }
        );

        return resp.data.patchPatient;
    } catch (err) {
        console.error('Error in patchPatient:', err);
        Sentry.captureException(err, {
            extra: {
                input,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'patchPatient'
            },
        });
        return null;
    }
};

const createExternalClinic = async (token: string, input: ExternalClinicInput) => {
    try {
        const resp = await callV3<CreateExternalClinicMutation, CreateExternalClinicMutationVariables>(
            token, 'client', CREATE_EXTERNAL_CLINIC, { input }
        );

        return resp.data.createExternalClinic;
    } catch (err) {
        console.error('Error in createExternalClinic:', err);
        Sentry.captureException(err, {
            extra: {
                input,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createExternalClinic'
            },
        });
        return null;
    }
};

const getExternalClinics = async (googlePlacesId: string) => {
    try {
        const resp = await callV3<GetExternalClinicsQuery, GetExternalClinicsQueryVariables>(
            null, 'public', GET_EXTERNAL_CLINICS, { googlePlacesId }
        );
        return resp.data.externalClinics[0];
    } catch (err) {
        console.error('Error in getExternalClinics:', err);
        Sentry.captureException(err, {
            extra: {
                googlePlacesId,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'getExternalClinics'
            },
        });
        return null;
    }
};

const createInboundMedicalRecordRequest = async (token: string, input: InboundMedicalRecordRequestInput) => {
    try {
        const resp = await callV3<
            CreateInboundMedicalRecordRequestMutation, CreateInboundMedicalRecordRequestMutationVariables
        >(
            token, 'client', CREATE_INBOUND_MEDICAL_RECORD_REQUEST, { input }
        );
        return resp.data.createInboundMedicalRecordRequest;
    } catch (err) {
        console.error('Error in createInboundMedicalRecordRequest:', err);
        Sentry.captureException(err, {
            extra: {
                input,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createInboundMedicalRecordRequest'
            },
        });
        return null;
    }
};

const createAccountCredit = async (token: string, data: AccountCreditInput): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateAccountCreditMutation, CreateAccountCreditMutationVariables>(
            token, 'client', CREATE_ACCOUNT_CREDIT, { data }
        );
        return { successful: !!resp.data.createAccountCredit };
    } catch (err) {
        console.error('Error createAccountCredit:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createAccountCredit'
            },
        });
        return { successful: false };
    }
};

const createClinicalRecord = async (token: string, data: ClinicalRecordInput): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateClinicalRecordMutation, CreateClinicalRecordMutationVariables>(
            token, 'client', CREATE_CLINICAL_RECORD, { data }
        );
        return { successful: !!resp.data.createClinicalRecord, data: resp.data.createClinicalRecord.id };
    } catch (err) {
        console.error('Error createClinicalRecord:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createClinicalRecord'
            },
        });
        const errorMessage = err ? `${err}` : 'Error createClinicalRecord';
        return { successful: false, errorMessage };
    }
};

const createInvoice = async (token: string, data: InvoiceInput): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateInvoiceMutation, CreateInvoiceMutationVariables>(
            token, 'client', CREATE_INVOICE, { data }
        );
        return { successful: !!resp.data.createInvoice, data: resp.data.createInvoice.id };
    } catch (err) {
        console.error('Error createInvoice:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createInvoice'
            },
        });
        const errorMessage = err ? `${err}` : 'Error createInvoice';
        return { successful: false, errorMessage };
    }
};

const createInvoiceItems = async (token: string, data: InvoiceItemInput[]): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateInvoiceItemsMutation, CreateInvoiceItemsMutationVariables>(
            token, 'client', CREATE_INVOICE_ITEMS, { data }
        );
        return {
            successful: !!resp.data.createInvoiceItems,
            data: resp.data.createInvoiceItems[0].invoice.postTaxTotal
        };
    } catch (err) {
        console.error('Error createInvoiceItems:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createInvoiceItems'
            },
        });
        const errorMessage = err ? `${err}` : 'Error createInvoiceItems';
        return { successful: false, errorMessage };
    }
};

const createTransaction = async (token: string, data: TransactionInput): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateTransactionMutation, CreateTransactionMutationVariables>(
            token, 'client', CREATE_TRANSACTION, { data }
        );
        const successful = resp.data.createTransaction.status === BillingTransactionStatusEnum.Successful;
        return {
            successful,
            errorMessage: successful ? undefined : resp.data.createTransaction.reasonForFailure || undefined
        };
    } catch (err) {
        console.error('Error createTransaction:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createTransaction'
            },
        });
        const errorMessage = err ? `${err}` : 'Error createTransaction';
        return { successful: false, errorMessage };
    }
};

const voidInvoice = async (token: string, data: InvoiceInputPartial): Promise<boolean> => {
    try {
        const resp = await callV3<PatchInvoiceMutation, PatchInvoiceMutationVariables>(
            token, 'client', PATCH_INVOICE, { data }
        );
        return !!resp.data.patchInvoice;
    } catch (err) {
        console.error('Error voidInvoice:', err);
        Sentry.captureException(err, {
            extra: {
                invoiceId: data.id,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'voidInvoice'
            },
        });
        return false;
    }
};

const createMembership = async (
    token: string,
    data: MembershipInput
): Promise<CreateMembershipResponse> => {
    try {
        const resp = await callV3<CreateMembershipMutation, CreateMembershipMutationVariables>(
            token, 'client', CREATE_MEMBERSHIP, { data }
        );
        return { successful: !!resp.data.createMembership };
    } catch (err) {
        console.error('Error createMembership:', err);
        Sentry.captureException(err, {
            extra: {
                data,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createMembership'
            },
        });
        const errorMessage = err ? `${err}` : 'Error createMembership';
        return { successful: false, errorMessage };
    }
};

const createPatientObject = async (
    patient: PatientStoreType,
    client: ClientStoreType
): Promise<string | null> => {
    const patientInput: PatientInput = {
        name: patient.name,
        birthdate: patient.birthdate,
        species: patient.species as CustomerPatientSpeciesEnum,
        breedLookup: { set: patient.breed.id },
        isMixed: patient.breed.isMixed,
        colorLookup: { set: patient.color.id },
        sex: patient.sex,
        spayedOrNeutered: patient.spayedOrNeutered,
        household: { set: client.household.id },
        recordsAvailability: patient.recordsAvailability
    };
    try {
        const createdPatient = patient.id
            ? await patchPatient(client.token, { id: patient.id, ...patientInput })
            : await createPatient(client.token, patientInput);
        if (!createdPatient) return null;
        return createdPatient.id;
    } catch (err) {
        console.error('Error creating patient: ', err);
        // eslint-disable-next-line no-alert
        alert(`Error creating patient - ${err}`);
    }

    return null;
};

const getOrCreateExternalClinic = async (externalClinic: ExternalClinicStoreType, token: string) => {
    let clinic = await getExternalClinics(externalClinic.placeId);

    if (!clinic) {
        const externalClinicInput: ExternalClinicInput = {
            name: externalClinic.name,
            address: externalClinic.formattedAddress,
            googlePlacesId: externalClinic.placeId,
            phone: externalClinic.phoneNumber
        };
        clinic = await createExternalClinic(token, externalClinicInput);
    }

    return clinic;
};

const createMedicalRecordRequest = async (
    patientId: string,
    externalClinic: ExternalClinicStoreType,
    token: string
): Promise<boolean> => {
    const clinic = await getOrCreateExternalClinic(externalClinic, token);

    if (!clinic?.id) return false;

    const medicalRecordInput: InboundMedicalRecordRequestInput = {
        patient: { set: patientId },
        externalClinic: clinic.id ? { set: clinic.id } : null,
        status: CustomerInboundMedicalRecordRequestStatusEnum.AwaitingMembership,
        clinicName: clinic.name,
        clinicAddress: clinic.address,
        clinicPhone: clinic.phone
    };

    try {
        const createdMedicalRecordRequest = await createInboundMedicalRecordRequest(
            token, medicalRecordInput
        );
        return !!createdMedicalRecordRequest;
    } catch (err) {
        console.error('Error creating medical record request: ', err);
        Sentry.captureException(err, {
            extra: {
                patientId,
                externalClinic,
                medicalRecordInput,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createPatientObject'
            },
        });
        return false;
    }
};

const createMembershipMutations = (
    token: string,
    patientId: string,
    membershipTier: CustomerMembershipTierEnum,
    householdId?: string,
    locationId?: string,
    timezone?: string,
    promo?: Promo | null,
    referringHousehold?: Pick<HouseholdType, 'id' | 'householdHash'>|null,
) => {
    const oneYearFromNow = (timezone = 'America/Los_Angeles') => DateTime.now()
        .setZone(timezone)
        .plus({ years: 1 })
        .toISODate();

    const createReferralAccountCredit = async (
        referringHouseholdId: string,
        oneYearFromNow: string,
        promoAmountOff: string,
        householdId = '',
    ): Promise<boolean> => {
        const input: AccountCreditInput = {
            household: { set: referringHouseholdId },
            amount: promoAmountOff,
            expirationDate: oneYearFromNow,
            creditType: BillingAccountCreditCreditTypeEnum.Referral,
            reason: `Referred household ${householdId}`,
        };

        const createdAccountCredit = await createAccountCredit(token, input);
        return createdAccountCredit.successful;
    };

    const createTransactionMutation = async (
        postTaxTotal: string,
        invoiceId: string,
        householdId?: string,
    ): Promise<CreateMembershipResponse> => {
        if (!householdId) return { successful: false };

        const input: TransactionInput = {
            amountAuth: postTaxTotal,
            household: { set: householdId },
            invoice: { set: invoiceId },
            method: BillingTransactionMethodEnum.ChargeCardOnFile,
        };

        const createdTransaction = await createTransaction(token, input);
        return createdTransaction;
    };

    const createInvoiceItemMutation = async (
        invoiceId: string, productId?: string
    ): Promise<CreateMembershipResponse> => {
        if (!productId) return { successful: false };

        const input: InvoiceItemInput[] = [{
            invoice: { set: invoiceId },
            product: { set: productId },
            quantity: '1',
        }];

        const createdInvoiceItems = await createInvoiceItems(token, input);
        return createdInvoiceItems;
    };

    const createInvoiceMutation = async (
        patientId: string, clinicalRecordId: string, promoId?: string
    ): Promise<CreateMembershipResponse> => {
        const input: InvoiceInput = {
            patient: { set: patientId },
            clinicalRecord: { set: clinicalRecordId },
            status: BillingInvoiceStatusEnum.Due,
            promo: promoId ? { set: promoId } : undefined,
        };

        const createdInvoice = await createInvoice(token, input);
        return createdInvoice;
    };

    const createClinicalRecordMutation = async (
        patientId: string,
        locationId?: string
    ): Promise<CreateMembershipResponse> => {
        const input: ClinicalRecordInput = {
            patient: { set: patientId },
            location: { set: locationId },
        };
        const createdClinicalRecord = await createClinicalRecord(token, input);
        return createdClinicalRecord;
    };

    const handleAnnualMemberships = async (
        patientId: string,
        membershipTier: CustomerMembershipTierEnum,
        householdId?: string,
        locationId?: string,
        promo?: Pick<PromoType, 'id' | 'code' | 'amountOff'> | null
    ): Promise<CreateMembershipResponse> => {
        // 1. Create clinical record
        const clinicalRecord = await createClinicalRecordMutation(patientId, locationId);
        if (!clinicalRecord.data) return clinicalRecord;

        // 2. Create invoice
        const invoice = await createInvoiceMutation(patientId, clinicalRecord.data, promo?.id);
        if (!invoice.data) return invoice;

        // 3. Create invoice item
        const invoiceItems = await createInvoiceItemMutation(
            invoice.data, tierToProductId[membershipTier]
        );
        if (!invoiceItems.data) return invoiceItems;
        if (invoiceItems.data.length) {
            // 4. Create transaction if the postTaxTotal is greater than 0
            if (invoiceItems.data !== '0.00') {
                const transaction = await createTransactionMutation(invoiceItems.data, invoice.data, householdId);
                if (!transaction.successful) {
                    await voidInvoice(token, { id: invoice.data, status: BillingInvoiceStatusEnum.Voided });
                    return transaction;
                }
            }
        }

        return { successful: true };
    };

    const createPromoAccountCredit = async (
        householdId: string,
        timezone: string,
        promo: Promo,
    ): Promise<boolean> => {
        if (!promo.amountOff) return false;

        const input: AccountCreditInput = {
            amount: promo.amountOff,
            household: { set: householdId },
            creditType: BillingAccountCreditCreditTypeEnum.Referral,
            expirationDate: oneYearFromNow(timezone),
            promo: { set: promo.id },
        };
        const createdAccountCredit = await createAccountCredit(token, input);
        return createdAccountCredit.successful;
    };

    const createMembershipMutation = async (
        token: string,
        patientId: string,
        membershipTier: CustomerMembershipTierEnum,
    ): Promise<CreateMembershipResponse> => {
        const input: MembershipInput = {
            patient: { set: patientId },
            tier: membershipTier,
        };
        const createdMembership = await createMembership(token, input);
        return createdMembership;
    };

    const handlePayAsYouGoMembership = async (
        patientId: string,
        householdId?: string,
        timezone?: string,
        promo?: Promo | null
    ): Promise<CreateMembershipResponse> => {
        const createdMembership = await createMembershipMutation(
            token,
            patientId,
            CustomerMembershipTierEnum.PayAsYouGo,
        );
        if (createdMembership.successful && promo && promo.amountOff && householdId && timezone) {
            await createPromoAccountCredit(
                householdId,
                timezone,
                promo
            );
        }
        return createdMembership;
    };

    const createPatientMembership = async (
        patientId: string,
        membershipTier: CustomerMembershipTierEnum,
        householdId?: string,
        locationId?: string,
        timezone?: string,
        promo?: Promo | null,
        referringHousehold?: Pick<HouseholdType, 'id' | 'householdHash'>|null,
    ): Promise<boolean> => {
        let createdMembership: CreateMembershipResponse = { successful: false };
        if (membershipTier === CustomerMembershipTierEnum.PayAsYouGo) {
            createdMembership = await handlePayAsYouGoMembership(
                patientId,
                householdId,
                timezone,
                promo
            );
        } else if (
            membershipTier === CustomerMembershipTierEnum.AllAccess
            || membershipTier === CustomerMembershipTierEnum.Essential
        ) {
            createdMembership = await handleAnnualMemberships(
                patientId,
                membershipTier,
                householdId,
                locationId,
                promo
            );
        }

        if (createdMembership.successful && referringHousehold && promo?.amountOff) {
            await createReferralAccountCredit(
                referringHousehold.id,
                oneYearFromNow(timezone),
                promo.amountOff,
                householdId,
            );
        }

        // eslint-disable-next-line no-alert
        if (createdMembership.errorMessage) alert(createdMembership.errorMessage);

        return createdMembership.successful;
    };

    return createPatientMembership(
        patientId,
        membershipTier,
        householdId,
        locationId,
        timezone,
        promo,
        referringHousehold
    );
};

const createAppointmentObject = async (appointment: AppointmentStoreType, patientId: string, token: string) => {
    if (!appointment.slot) return null;

    const appointmentInput: AppointmentInput = {
        patient: { set: patientId },
        staff: { set: appointment.slot.staff?.id },
        slot: { set: appointment.slot.id },
        location: { set: appointment.slot.location.id },
        start: appointment.slot.start,
        end: appointment.slot.end,
        reasonForVisitDescription: appointment.reasonForVisitDescription,
        appointmentType: StaffAppointmentAppointmentTypeEnum.Standard,
        source: StaffAppointmentSourceEnum.Website
    };

    try {
        const createdAppointment = await createAppointment(token, appointmentInput);
        return createdAppointment;
    } catch (err) {
        console.error('Error confirming booking: ', err);
        Sentry.captureException(err, {
            extra: {
                patientId,
                appointmentInput,
                path: 'src/patientOnboarding/patientOnboarding.helpers',
                function: 'createAppointmentObject'
            },
        });
        // eslint-disable-next-line no-alert
        alert('Your selected time is no longer available. All information you entered is saved - just pick another time.');
    }

    return null;
};

const ensurePatient = async (
    patient: PatientStoreType,
    client: ClientStoreType,
    updatePatient: (p: PatientStoreType)=> void
): Promise<string | null> => {
    if (patient.id) return patient.id;
    const newId = await createPatientObject(patient, client);
    if (!newId) return null;
    updatePatient({ ...patient, id: newId });
    return newId;
};

const ensureMedicalRecordRequested = async (
    patientId: string,
    externalClinic: ExternalClinicStoreType,
    token: string,
    isMedicalRecordRequested: boolean,
    updateIsMedicalRecordRequested: (b: boolean)=> void
): Promise<void> => {
    if (externalClinic?.placeId && !isMedicalRecordRequested) {
        const requested = await createMedicalRecordRequest(
            patientId,
            externalClinic,
            token
        );
        updateIsMedicalRecordRequested(requested);
    }
};

const ensureMembership = async (
    patientId: string,
    patient: PatientStoreType,
    client: ClientStoreType,
    location: string,
    isMembershipCreated: boolean,
    updateIsMembershipCreated: (b: boolean)=> void
): Promise<boolean> => {
    if (isMembershipCreated) return true;
    const created = await createMembershipMutations(
        client.token,
        patientId,
        patient.membershipTier,
        client.household.id,
        location,
        client.household.timezone,
        patient.promo,
        patient.referringHousehold
    );
    if (created) updateIsMembershipCreated(true);
    return created;
};

const makeAppointment = async (
    appointment: AppointmentStoreType,
    patientId: string,
    token: string,
    updateAppointment: (a: AppointmentStoreType)=> void
): Promise<string | null> => {
    const created = await createAppointmentObject(appointment, patientId, token);
    if (!created) return null;
    updateAppointment({ ...appointment, createdAppointment: created });
    return created.id;
};

/**
 * Creates entities for patient onboarding.
 * The following entities are created by this script:
 * 1. Patient
 * 2. Medical record request
 * 3. Membership
 * 4. Appointment
 *
 * @param patient - The patient object.
 * @param client - The client object.
 * @param externalClinic - The external clinic object.
 * @param isMedicalRecordRequested - Indicates if medical record is requested.
 * @param isMembershipCreated - Indicates if membership is created.
 * @param appointment - The appointment object.
 * @param promoCode - The promo code.
 * @param updatePatient - The function to update the patient object.
 * @param updateIsMedicalRecordRequested - The function to update the medical record request status.
 * @param updateIsMembershipCreated - The function to update the membership creation status.
 * @param updateAppointment - The function to update the appointment object.
 * @param locationId - the url location query parameter id
 * @returns A string with the appointment ID or undefined if one of the entities could not be created.
 */
export const createPatientRelatedEntities = async (
    patient: PatientStoreType,
    client: ClientStoreType,
    externalClinic: ExternalClinicStoreType,
    isMedicalRecordRequested: boolean,
    isMembershipCreated: boolean,
    appointment: AppointmentStoreType,
    updatePatient: (updatedPatient: PatientStoreType)=> void,
    updateIsMedicalRecordRequested: (isRequested: boolean)=> void,
    updateIsMembershipCreated: (isCreated: boolean)=> void,
    updateAppointment: (updatedAppointment: AppointmentStoreType)=> void,
    locationId?: string,
): Promise<string | null> => {
    // 1) Ensure patient
    const patientId = await ensurePatient(patient, client, updatePatient);
    if (!patientId) return null;

    // 2) Maybe request medical record
    await ensureMedicalRecordRequested(
        patientId,
        externalClinic,
        client.token,
        isMedicalRecordRequested,
        updateIsMedicalRecordRequested
    );

    // 3) Ensure membership
    const location = locationId ?? appointment.slot?.location.id as string;
    const membershipOk = await ensureMembership(
        patientId,
        patient,
        client,
        location,
        isMembershipCreated,
        updateIsMembershipCreated
    );
    if (!membershipOk) return null;

    // Add timeout to ensure membership is created
    // and show dogs animation as a loading screen
    await new Promise((resolve) => {
        setTimeout(resolve, 1000);
    });

    // 4) Create appointment
    return makeAppointment(
        appointment,
        patientId,
        client.token,
        updateAppointment
    );
};

export const calculatePromoDiscountAmount = (
    promo: Promo | null,
    membershipPrice: string,
) => {
    if (membershipPrice === '0') return '0';
    if (!promo) return membershipPrice;

    const { amountOff } = promo;

    if (amountOff) {
        if (parseFloat(amountOff) >= parseFloat(membershipPrice)) {
            return '0';
        }

        return (parseFloat(membershipPrice) - parseFloat(amountOff)).toFixed(2);
    }

    return membershipPrice;
};

/**
 * Maps membership products from a GraphQL query result to an array of Membership objects.
 *
 * This function handles two types of memberships:
 * 1. Pay-as-you-go (PAYG) membership
 * 2. Regular memberships (e.g., All Access)
 *
 * When new membership products are added, they should be added to the productTierMatcher.
 *
 * @param products - The membership products data returned from GetMembershipProductsQuery
 * @returns An array of Membership objects with tier and price information
 */
export const mapMembershipProducts = (
    products: GetMembershipProductsAndComprehensiveExamQuery,
): Membership[] => {
    /* the Upgrade Product should never be included .. it should only be used
    when calculating pro-rated AA amount for current SA member */
    const membershipsToSkip = [
        ALL_ACCESS_UPGRADE_PRODUCT_ID,
    ];

    const memberships = [];
    // When new memberships are added, add them to the enum and map them here
    const productTierMatcher: Record<string, CustomerMembershipTierEnum> = {
        [ALL_ACCESS_PRODUCT_ID]: CustomerMembershipTierEnum.AllAccess,
        [ESSENTIAL_PRODUCT_ID]: CustomerMembershipTierEnum.Essential,
    };

    if (products?.comprehensiveExam?.length) {
        memberships.push({
            productId: products.comprehensiveExam[0].id,
            membershipTier: CustomerMembershipTierEnum.PayAsYouGo,
            description: 'Default plan',
            membershipPrice: removeTrailingZeros(products.comprehensiveExam[0].salePrice),
        });
    }

    if (products?.memberships?.length) {
        products.memberships.filter(
            (m) => !membershipsToSkip.includes(m.id)
        ).forEach((membershipProduct) => {
            memberships.push({
                productId: membershipProduct.id,
                membershipTier: productTierMatcher[membershipProduct.id],
                description: 'Most popular',
                membershipPrice: removeTrailingZeros(membershipProduct.salePrice),
            });
        });
    }

    return memberships;
};
